import {
  COLORS,
  BUTTON_SIZE,
  BUTTON_TYPE,
  DIVIDER_SIZE,
  EXAM_TYPES,
  TEXT_SIZES,
  USER_ROLES,
  TEXT_ALIGN,
} from '@helpers/constants'
import Button from '@components/Button'
import { getImageSrc } from '@helpers/utilities'
import BlockRenderer from '@views/BlockRenderer'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Divider from '@components/Divider'
import { useContext, useState } from 'react'
import { AppContext } from '@state/context'
import { useCreateExamAttempt } from '@hooks/mutations/useCreateExamAttempt'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Icon from '@components/Icon'
import {
  getHighestAttempt,
  getLatestAttempt,
  getFormattedPercent,
  StatusCorner,
  getAttemptsByStatus,
} from '@helpers/exams'
import { useDeleteExamAttempts } from '@hooks/mutations/useDeleteExamAttempts'

const contentIsEmpty = (content) => {
  const contentObject = JSON.parse(content)
  return contentObject === null || contentObject?.isEmpty === true
}

const smallPillClasses =
  'font-secondary text-xs rounded-full whitespace-nowrap inline-flex items-center text-white bg-primaryGreen h-[22px] px-2.5'

const largePillClasses =
  'rounded-full whitespace-nowrap inline-flex items-center text-white h-[42px] px-4 mb-4 gap-x-2'

const isYesterdayOrOlder = (p_date) => {
  const date = new Date(p_date)
  const today = new Date()

  today.setHours(0, 0, 0, 0)

  // Check if date is before today (meaning it's yesterday or older)
  return date < today
}

const ExamBlock = ({ exam, onStartClick }) => {
  const {
    id: examId,
    type,
    name,
    image,
    description,
    additionalDescriptionOne,
    additionalDescriptionTwo,
    passingPercentage,
    maxAttempts,
    customerAttempts,
  } = exam

  const [state, dispatch] = useContext(AppContext)

  const {
    app: {
      userId,
      userRole,
      currentSessionLatestFinishedExam,
      currentExamAttemptInProgressId,
    },
  } = state

  const attemptsLeft = Math.max(0, maxAttempts - customerAttempts.length)

  const noAdditionalDescriptions =
    contentIsEmpty(additionalDescriptionOne) &&
    contentIsEmpty(additionalDescriptionTwo)

  const [currentDescription, setCurrentDescription] = useState(
    additionalDescriptionOne
  )

  const highestPassed = getHighestAttempt(customerAttempts, true)
  const highestFailed = getHighestAttempt(customerAttempts, false)
  const highestNotFinished = getHighestAttempt(customerAttempts, null)
  const latestAttempt = getLatestAttempt(customerAttempts)

  const passedAttempts = getAttemptsByStatus(customerAttempts, true)
  const failedAttempts = getAttemptsByStatus(customerAttempts, false)
  const notFinishedAttempts = getAttemptsByStatus(customerAttempts, null)

  const getStatusCorner = () => {
    if (!latestAttempt) return null

    if (highestPassed) {
      return <StatusCorner passed={true} size='large' />
    } else {
      if (attemptsLeft > 0) {
        return <StatusCorner size='large' />
      } else {
        if (currentExamAttemptInProgressId === latestAttempt?.id) {
          return <StatusCorner size='large' />
        } else {
          return <StatusCorner passed={false} size='large' />
        }
      }
    }
  }

  // --- For self approval

  const { post: postCreate, isPosting: isPostingCreate } =
    useCreateExamAttempt()

  const { post: postDelete, isPosting: isPostingDelete } =
    useDeleteExamAttempts()

  const onMarkAsDoneClick = async () => {
    if (userRole === USER_ROLES.STUDENT) {
      try {
        await postCreate({
          exam: examId,
          customer: userId,
          isPassed: true,
        })
      } catch (error) {
        console.log('🚀 ~ onMarkAsDoneClick ~ error:', error)
      }
    }
  }

  const onUndoClick = async () => {
    if (userRole === USER_ROLES.STUDENT && highestPassed) {
      try {
        await postDelete({
          ids: [highestPassed.id],
        })
      } catch (error) {
        console.log('🚀 ~ onUndoClick ~ error:', error)
      }
    }
  }

  return (
    <>
      <div className={'relative bg-lightGrey p-7 whitespace-pre-line'}>
        {/* Passed / Failed / In progress */}
        {getStatusCorner()}

        <div className={`w-full flex`}>
          <div className={`w-2/3 flex flex-col items-start`}>
            {type === parseInt(EXAM_TYPES.QUIZ.value) && (
              // For quiz
              <>
                {/* Passed / Failed - Right now */}
                {currentSessionLatestFinishedExam?.id === examId && (
                  <>
                    {latestAttempt && latestAttempt.isPassed === true && (
                      <div className={`${largePillClasses} bg-examGreen`}>
                        <Heading
                          headingLevel='h4'
                          size={TEXT_SIZES.XLARGE}
                          color={COLORS.WHITE.TEXT}
                        >
                          {getFormattedPercent(
                            latestAttempt.correctCount,
                            latestAttempt.totalCount
                          )}
                        </Heading>
                        <Text color={COLORS.WHITE.TEXT}>Godkänt</Text>
                      </div>
                    )}

                    {latestAttempt && latestAttempt.isPassed === false && (
                      <div className={`${largePillClasses} bg-examRed`}>
                        <Heading
                          headingLevel='h4'
                          size={TEXT_SIZES.XLARGE}
                          color={COLORS.WHITE.TEXT}
                        >
                          {getFormattedPercent(
                            latestAttempt.correctCount,
                            latestAttempt.totalCount
                          )}
                        </Heading>
                        <Text color={COLORS.WHITE.TEXT}>Icke godkänt</Text>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <Heading headingLevel='h3' size={TEXT_SIZES.XLARGE}>
              {name}
            </Heading>

            {description && (
              <div className={`self-stretch`}>
                <BlockRenderer content={description} />
              </div>
            )}
          </div>
        </div>

        <Divider size={DIVIDER_SIZE.BASE} />

        <div className={`w-full flex justify-between items-center`}>
          {!noAdditionalDescriptions ? (
            <ol className={'flex flex-wrap'}>
              {!contentIsEmpty(additionalDescriptionOne) && (
                <li className={'mr-2'}>
                  <Button
                    type={
                      currentDescription === additionalDescriptionOne
                        ? BUTTON_TYPE.PRIMARY
                        : BUTTON_TYPE.SECONDARY
                    }
                    size={BUTTON_SIZE.MEDIUM}
                    handleClick={() => {
                      setCurrentDescription(additionalDescriptionOne)
                    }}
                  >
                    Uppgift
                  </Button>
                </li>
              )}

              {!contentIsEmpty(additionalDescriptionTwo) && (
                <li className={'mr-2'}>
                  <Button
                    type={
                      currentDescription === additionalDescriptionTwo
                        ? BUTTON_TYPE.PRIMARY
                        : BUTTON_TYPE.SECONDARY
                    }
                    size={BUTTON_SIZE.MEDIUM}
                    handleClick={() => {
                      setCurrentDescription(additionalDescriptionTwo)
                    }}
                  >
                    Bra att veta
                  </Button>
                </li>
              )}
            </ol>
          ) : (
            <div></div>
          )}

          {type === parseInt(EXAM_TYPES.QUIZ.value) ? (
            // For Quiz
            <div className={'flex items-end gap-x-5'}>
              <div className={'flex flex-col items-center gap-y-0.5'}>
                <p className={smallPillClasses}>{passingPercentage}% rätt</p>
                <p className='text-xs'>för godkänt</p>
              </div>

              {userRole === USER_ROLES.STUDENT && !highestPassed && (
                <div className={'flex flex-col items-end gap-y-0.5'}>
                  <div className={'flex gap-x-0.5'}>
                    {Array(maxAttempts)
                      .fill()
                      .map((_, index) => {
                        return maxAttempts - attemptsLeft > index ? (
                          <Icon key={index} icon='HeartOutline' />
                        ) : (
                          <Icon key={index} icon='HeartFilled' />
                        )
                      })}
                  </div>
                  <p className='text-xs'>{attemptsLeft} försök kvar</p>
                </div>
              )}
            </div>
          ) : (
            <div></div>
          )}
        </div>

        <Divider size={DIVIDER_SIZE.SMALL} />

        <div className={'w-full flex p-2 bg-white'}>
          <div className={`h-full p-2 ${image ? 'w-2/3' : 'w-full '}`}>
            <BlockRenderer content={currentDescription} />
          </div>

          {image && (
            <div className='w-1/3 self-start'>
              <div className={'w-full pb-[100%] relative'}>
                <img
                  className={'absolute w-full h-full object-cover '}
                  src={getImageSrc(image)}
                  alt=''
                />
              </div>
            </div>
          )}
        </div>

        <div className={'w-full flex flex-col items-center mt-7'}>
          {type === parseInt(EXAM_TYPES.QUIZ.value) ? (
            // For Quiz
            <>
              {!highestPassed && (
                <>
                  {attemptsLeft > 0 ? (
                    <>
                      {latestAttempt &&
                      !isYesterdayOrOlder(latestAttempt.updated_at) ? (
                        <>
                          <Text align={TEXT_ALIGN.CENTER} extraClasses='mb-3'>
                            <b>Du kan göra ett nytt försök i morgon.</b>
                            <br />
                            Öva gärna under tiden.
                          </Text>
                          <Button
                            type={BUTTON_TYPE.PRIMARY}
                            size={BUTTON_SIZE.LARGE}
                            disabled
                            icon={'PadLock'}
                            iconLeft
                          >
                            Starta test
                          </Button>
                        </>
                      ) : (
                        <Button
                          handleClick={onStartClick}
                          type={BUTTON_TYPE.PRIMARY}
                          size={BUTTON_SIZE.LARGE}
                        >
                          Starta test
                        </Button>
                      )}
                    </>
                  ) : (
                    <Text>Du har inga försök kvar på detta test.</Text>
                  )}
                </>
              )}
            </>
          ) : (
            // For Self Approval
            <>
              {highestPassed ? (
                <>
                  <Text>Avsnittet är klart!</Text>
                  {/* <ButtonWithLoader
                    type={
                      isPostingDelete
                        ? BUTTON_TYPE.PRIMARY
                        : BUTTON_TYPE.PRIMARY_OUTLINE
                    }
                    size={BUTTON_SIZE.LARGE}
                    isLoading={isPostingDelete}
                    disabledText={'Ångra'}
                    enabledText={'Ångra'}
                    handleClick={onUndoClick}
                    icon={isPostingDelete ? undefined : 'UndoArrow'}
                    iconRight={isPostingDelete ? false : true}
                  /> */}
                </>
              ) : (
                <>
                  <ButtonWithLoader
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.LARGE}
                    isLoading={isPostingCreate}
                    disabledText={'Markera som klar'}
                    enabledText={'Markera som klar'}
                    handleClick={onMarkAsDoneClick}
                    icon={isPostingCreate ? undefined : 'MarkAsDoneCheck'}
                    iconRight={isPostingCreate ? false : true}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default ExamBlock
